<template>
  <div class="transaction">
    <app-cells position="between">
      <h1 class="title">Cashflow</h1>
      <app-button @click.native="onShowExportModal" v-if="is_general_manager">Export</app-button>
    </app-cells>
    <app-cells>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          shortcut="last30Days"
          @is-hidden="onIsHiddenPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          noClearButton
        />
        <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
      <app-input
        @input.native="onTransactionSearch"
        v-model="search"
        class="input--200"
        type="search"
        placeholder="Search"
        name="search"
      />
      <app-select
        v-model="filter_account"
        @input="onChangeFilter"
        :reduce="item => item.id"
        :options="account_options"
        :filterable="true"
        :clearable="true"
        :searchable="true"
        placeholder="Account"
        class="select--300"
      />
      <app-select
        v-model="filter_supplier"
        @search="onSupplierSearch"
        @input="onChangeFilter"
        :options="supplier_options"
        label="trade_name"
        placeholder="Supplier"
        class="select--300"
      />
      <app-filters
        v-model="filter_type"
        :filters="filter_type_options"
        name="filterType"
        @change="onChangeFilter"
      />
    </app-cells>
    <app-cells>
      <app-button @click.native="onShowCreateModal('income')">
        <img src="@/assets/img/icon-income.svg" alt="Income">
        Income
      </app-button>
      <app-button @click.native="onShowCreateModal('outcome')" theme="outcome">
        <img src="@/assets/img/icon-outcome.svg" alt="Income">
        Outcome
      </app-button>
      <app-button @click.native="onShowCreateModalTransfer" theme="info">
        <img src="@/assets/img/icon-transfer-transaction.svg" alt="Income">
        Transfer
      </app-button>
      <app-button @click.native="onShowBalanceModal" theme="warn">
        Posting balance
      </app-button>
    </app-cells>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <div class="transaction__table" :class="{ 'transaction__table--loading': table_preloader }">
        <div class="transaction__table-row transaction__table-row--head">
          <div class="transaction__table-col">Date</div>
          <div class="transaction__table-col">Debit</div>
          <div class="transaction__table-col">Credit</div>
          <div class="transaction__table-col">Item / Description</div>
          <div class="transaction__table-col">Account</div>
          <div class="transaction__table-col">Balance</div>
          <div class="transaction__table-col"></div>
        </div>
        <div v-for="transaction of table_data" :key="transaction.id" class="transaction__table-row">
          <div class="transaction__table-col">
            <b v-tooltip="{content: tooltipContent(transaction), placement: 'right', offset: 10, html: true}">
              {{ dateFormat(transaction.date) }}
            </b>
          </div>
          <div class="transaction__table-col">
            <template v-if="transaction.is_transfer">
              <b v-if="transaction.transfer_transaction_to" class="transaction__table-amount transaction__table-amount--green">
                {{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.transfer_transaction_to.amount) }} {{ transaction.transfer_transaction_to.account.currency.code }}
              </b>
            </template>
            <template v-else>
              <b v-if="transaction.type === 1" class="transaction__table-amount transaction__table-amount--green">
                {{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency.code }}
              </b>
            </template>
          </div>
          <div class="transaction__table-col">
            <template v-if="transaction.is_transfer">
              <b class="transaction__table-amount transaction__table-amount--red">
                -{{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency.code }}
              </b>
            </template>
            <template v-else>
              <b v-if="transaction.type === 2" class="transaction__table-amount transaction__table-amount--red">
                -{{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency.code }}
              </b>
            </template>
          </div>
          <div class="transaction__table-col transaction__table-col--flex-col">
            <b v-if="transaction.is_transfer">Transfer funds</b>
            <template v-else>
              <button
                v-if="transaction.items.length > 1"
                v-tooltip="{
                  content: tooltipContentCashflowItems(transaction),
                  placement: 'right',
                  offset: 10,
                  html: true,
                  trigger: 'click',
                  classes: 'tooltip-cashflow'
                }"
                class="transaction__table-btn"
              >
                {{ transaction.items.length }} items
                <span v-if="transaction.responsible">{{ transaction.responsible.full_name }}</span>
              </button>
              <template v-else>
                <b v-if="transaction.type === 1">{{ transaction.items[0]?.item.name_revenue }} <span v-if="transaction.responsible" class="transaction__table-description">{{ transaction.responsible.full_name }}</span></b>
                <b v-if="transaction.type === 2">{{ transaction.items[0]?.item.name_expense }} <span v-if="transaction.responsible" class="transaction__table-description">{{ transaction.responsible.full_name }}</span></b>
              </template>
            </template>
            <span v-if="transaction.description" class="transaction__table-description">{{ transaction.description }}</span>
            <b v-if="transaction.supplier" class="transaction__table-description">{{ transaction.supplier.trade_name }}</b>
          </div>
          <div class="transaction__table-col transaction__table-col--flex-col">
            <template v-if="transaction.is_transfer">
              <template v-if="transaction.transfer_transaction_to">
                <b>{{ transaction.transfer_transaction_to.account.name }}</b>
                <b v-if="transaction.transfer_transaction_to.reference" class="transaction__table-description">{{ transaction.transfer_transaction_to.reference }}</b>
              </template>
              <b>{{ transaction.account.name }}</b>
              <b v-if="transaction.reference" class="transaction__table-description">{{ transaction.reference }}</b>
            </template>
            <template v-else>
              <b>{{ transaction.account.name }}</b>
              <b v-if="transaction.reference" class="transaction__table-description">{{ transaction.reference }}</b>
            </template>
          </div>
          <div class="transaction__table-col">
            <template v-if="transaction.is_transfer">
              <span
                v-if="transaction.transfer_transaction_to && transaction.transfer_transaction_to.account_balance !== null"
                class="transaction__table-balance transaction__table-balance--plus"
              >
                {{ new Intl.NumberFormat('en-US').format(transaction.transfer_transaction_to.account_balance) }} {{ transaction.transfer_transaction_to.account.currency.code }}
              </span>
              <template v-else></template>
              <br>
              <span
                v-if="transaction.account_balance !== null"
                class="transaction__table-balance transaction__table-balance--minus"
              >
                {{ new Intl.NumberFormat('en-US').format(transaction.account_balance) }} {{ transaction.account.currency.code }}
              </span>
              <template v-else></template>
            </template>
            <span
              v-else
              class="transaction__table-balance"
              :class="{'transaction__table-balance--plus': transaction.type === 1, 'transaction__table-balance--minus': transaction.type === 2, }"
            >
              <template v-if="transaction.account_balance !== null">
                {{ new Intl.NumberFormat('en-US').format(transaction.account_balance) }} {{ transaction.account.currency.code }}
              </template>
              <template v-else></template>
            </span>
          </div>
          <div v-if="!transaction.reconciliation" class="transaction__table-col transaction__table-col--flex">
            <button @click="onShowEditModal(transaction)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(transaction, transaction.is_transfer)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </div>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_data.length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
    <modal
      name="balance-modal"
      :width="500"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('balance-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Posting balance</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckBalanceForm" class="arrange-popup">
            <div class="arrange-popup__part">
              <div class="transfer-sortable transfer-sortable--full">
                <div v-for="(element, index) in form_balance" :key="`own_item_${index}`" class="transfer-sortable__item transfer-sortable__item--mb-20">
                  <div class="transfer-sortable__row transfer-sortable__row--balance transfer-sortable__row--pr">
                    <app-form-group label="Account" required class="form-group--no-indent">
                      <app-select
                        v-model="element.account"
                        :options="account_options"
                        :clearable="false"
                        placeholder="Account"
                        :error="$v.form_balance.$each[index].account.$error"
                      >
                      </app-select>
                      <template #error>
                        <div v-if="$v.form_balance.$each[index].account.$dirty && !$v.form_balance.$each[index].account.required">Required field</div>
                      </template>
                    </app-form-group>
                    <app-form-group label="Balance" :label-for="`balance-${index}`" required class="form-group--no-indent">
                      <app-fin-number
                        v-model="element.balance"
                        :error="$v.form_balance.$each[index].balance.$error"
                        :id="`balance-${index}`"
                        placeholder="0"
                        name="balance"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.$each[index].balance.$dirty && !$v.form_balance.$each[index].balance.required">Required field</div>
                      </template>
                    </app-form-group>
                    <button
                      v-if="index"
                      @click="onRemoveBalanceItem(index)"
                      class="transfer-sortable__close"
                      type="button"
                    >
                      <img src="@/assets/img/delete-transfer.svg" alt="">
                    </button>
                    <button v-if="!index" @click="onAddBalanceItem" type="button" class="transfer-sortable__add"></button>
                  </div>
                </div>
              </div>
            </div>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_balance" :disabled="$v.form_balance.$error">Post</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="transfer-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('transfer-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ transaction_to_edit ? 'Edit transfer' : 'Transfer funds' }}</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormTransfer">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="transfer_form.date"
                placeholder="Specify a date"
                :error="$v.transfer_form.date.$error"
              />
              <template #error>
                <div v-if="$v.transfer_form.date.$dirty && !$v.transfer_form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="From account" required>
              <app-select
                v-model="transfer_form.from_account"
                :options="account_options_transfer"
                :clearable="false"
                placeholder="Account"
                :error="$v.transfer_form.from_account.$error"
              >
              </app-select>
              <template #error>
                <div v-if="$v.transfer_form.from_account.$dirty && !$v.transfer_form.from_account.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="To account" required>
              <app-select
                v-model="transfer_form.to_account"
                :options="account_options_transfer"
                :clearable="false"
                placeholder="Account"
                :error="$v.transfer_form.to_account.$error"
              >
              </app-select>
              <template #error>
                <div v-if="$v.transfer_form.to_account.$dirty && !$v.transfer_form.to_account.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group
              :label="transfer_form.from_account && transfer_form.to_account && transfer_form.from_account?.currency?.code !== transfer_form.to_account?.currency?.code ? 'Given amount' : 'Transfer amount'"
              label-for="transfer_amount"
              required
            >
              <app-fin-number
                v-model="transfer_form.from_amount"
                :error="$v.transfer_form.from_amount.$error"
                id="transfer_amount"
                placeholder="0"
                name="transfer_amount"
              />
              <template #error>
                <div v-if="$v.transfer_form.from_amount.$dirty && !$v.transfer_form.from_amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group
              v-if="transfer_form.from_account && transfer_form.to_account && transfer_form.from_account?.currency?.code !== transfer_form.to_account?.currency?.code"
              label="Received amount"
              label-for="received_amount"
              required
            >
              <app-fin-number
                v-model="transfer_form.to_amount"
                :error="$v.transfer_form.to_amount.$error"
                id="received_amount"
                placeholder="0"
                name="received_amount"
              />
              <template #error>
                <div v-if="$v.transfer_form.to_amount.$dirty && !$v.transfer_form.to_amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Description" label-for="description">
              <app-input
                v-model="transfer_form.description"
                id="description"
                placeholder="Description"
                name="description"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_transfer" :disabled="$v.transfer_form.$error">
                {{ !transaction_to_edit ? 'Transfer' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="transaction-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('transaction-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">
          <template v-if="!transaction_to_edit">
            {{ transaction_type === 'income' ? 'Add income' : 'Add outcome' }}
          </template>
          <template v-else>Edit transaction</template>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm" class="arrange-popup">
            <app-grid>
              <template #item-1>
                <app-form-group label="Account" required v-if="transaction_type === 'income'">
                  <app-select
                    v-model="form.account"
                    :options="account_options_income"
                    :clearable="false"
                    placeholder="Account"
                    :error="$v.form.account.$error"
                  >
                  </app-select>
                  <template #error>
                    <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Account" required v-else>
                  <app-select
                    v-model="form.account"
                    :options="account_options_outcome"
                    :clearable="false"
                    placeholder="Account"
                    :error="$v.form.account.$error"
                  >
                  </app-select>
                  <template #error>
                    <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group v-if="form.account?.reference" label="Reference" label-for="reference" required>
                  <app-input
                    v-model="form.reference"
                    id="reference"
                    name="reference"
                    placeholder="Reference"
                    :error="$v.form.reference.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.reference.$dirty && !$v.form.reference.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Date" required>
                  <app-date-picker
                    v-model="form.date"
                    placeholder="Specify a date"
                    :error="$v.form.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Supplier">
                  <app-select
                    @search="onSupplierSearch"
                    v-model="form.supplier"
                    :options="supplier_options"
                    :filterBy="() => true"
                    :display-field="option => `${option.trade_name === option.legal_name ? option.trade_name : `${option.trade_name} (${option.legal_name})`}`"
                    label="trade_name"
                    placeholder="Supplier"
                  >
                  </app-select>
                </app-form-group>
                <app-form-group label="Responsible">
                  <app-select
                    v-model="form.responsible"
                    :options="employee_options"
                    label="full_name"
                    placeholder="Responsible"
                  >
                  </app-select>
                </app-form-group>
              </template>
            </app-grid>
            <div class="arrange-popup__part">
              <div class="arrange-popup__block-title arrange-popup__block-title--fz13"><span>Items</span></div>
              <div class="transfer-sortable transfer-sortable--cashflow-2 transfer-sortable--full">
                <div v-for="(element, index) in form.items" :key="`own_item_${index}`" class="transfer-sortable__item transfer-sortable__item--mb-20">
                  <div class="transfer-sortable__row transfer-sortable__row--pr">
                    <app-form-group label="Amount" :label-for="`amount-${index}`" required class="form-group--no-indent">
                      <app-fin-number
                        v-model="element.amount"
                        :error="$v.form.items.$each[index].amount.$error"
                        :id="`amount-${index}`"
                        placeholder="0"
                        name="amount"
                      />
                      <template #error>
                        <div v-if="$v.form.items.$each[index].amount.$dirty && !$v.form.items.$each[index].amount.required">Required field</div>
                      </template>
                    </app-form-group>
                    <app-form-group label="Transaction item" v-if="transaction_type === 'income'" class="form-group--no-indent" required>
                      <app-select
                        v-model="element.item"
                        :options="transaction_items_options_income"
                        :clearable="false"
                        label="name_revenue"
                        placeholder="Transaction item"
                        :error="$v.form.items.$each[index].item.$error"
                      >
                      </app-select>
                      <template #error>
                        <div v-if="$v.form.items.$each[index].item.$dirty && !$v.form.items.$each[index].item.required">Required field</div>
                      </template>
                    </app-form-group>
                    <app-form-group label="Transaction item" v-else class="form-group--no-indent" required>
                      <app-select
                        v-model="element.item"
                        :options="transaction_items_options_outcome"
                        :clearable="false"
                        label="name_expense"
                        placeholder="Transaction item"
                        :error="$v.form.items.$each[index].item.$error"
                      >
                      </app-select>
                      <template #error>
                        <div v-if="$v.form.items.$each[index].item.$dirty && !$v.form.items.$each[index].item.required">Required field</div>
                      </template>
                    </app-form-group>
                    <button
                      v-if="index"
                      @click="onRemoveItem(index)"
                      class="transfer-sortable__close"
                      type="button"
                    >
                      <img src="@/assets/img/delete-transfer.svg" alt="">
                    </button>
                    <button v-if="!index" @click="onAddItem" type="button" class="transfer-sortable__add"></button>
                  </div>
                </div>
              </div>
            </div>
            <app-form-group label="Notes" label-for="description">
              <app-textarea
                v-model="form.description"
                rows="5"
                placeholder="Write a note"
                id="description"
                name="description"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit" :disabled="$v.form.$error">
                {{ !transaction_to_edit ? 'Add' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete {{ transaction_type === 'transfer' ? 'transfer' : 'transaction' }}
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this {{ transaction_type === 'transfer' ? 'transfer' : 'transaction' }}?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteTransaction" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="exportModal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('exportModal')" class="popup__close" type="button"></button>
        <div class="popup__title">Export</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormExport">
            <app-form-group label="Date Range" required>
              <app-date-picker
                v-model="form_export.period"
                range
                :clearable="false"
                placeholder="Date Range"
                :error="$v.form_export.period.$error"
              />
              <template #error>
                <div v-if="$v.form_export.period.$dirty && !$v.form_export.period.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Accounts" required>
              <app-select
                v-model="form_export.accounts"
                :reduce="item => item.id"
                :options="account_options"
                multiple
                placeholder="Accounts"
                :error="$v.form_export.accounts.$error"
              />
              <template #error>
                <div v-if="$v.form_export.accounts.$dirty && !$v.form_export.accounts.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Supplier">
              <app-select
                v-model="form_export.supplier"
                @search="onSupplierSearch"
                :options="supplier_options"
                :filterBy="() => true"
                :display-field="option => `${option.trade_name === option.legal_name ? option.trade_name : `${option.trade_name} (${option.legal_name})`}`"
                label="trade_name"
                placeholder="Supplier"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submitExport" :disabled="$v.form_export.$error">Export</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import {
  deleteFinanceCashflow,
  deleteFinanceCashflowTransfer,
  getAdministrativeSuppliers,
  getEmployeesListSearch,
  getFinanceAccounts,
  getFinanceCashflow,
  getFinanceTransactionsItemsList,
  patchFinanceCashflow,
  postFinanceAccountsBalanceReconciliation,
  postFinanceCashflow,
  postFinanceCashflowTransfer,
  putFinanceCashflowTransfer,
  getFinanceCashflowExport
} from '@/http'
import moment from 'moment'
import { format, parse, parseISO } from 'date-fns'
import { required, requiredIf } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

export default {
  name: 'FinanceCashflow',
  components: { VueCtkDateTimePicker },
  data() {
    return {
      is_loaded: false,
      table_preloader: false,
      date_picker_error: false,
      search: '',
      custom_shortcuts: [
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
        {
          key: 'next90Days',
          label: 'Next 90 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(90, 'days')
            }
          }
        },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      filter_dates: null,
      table_data: [],
      transaction_type: '',
      form: {
        date: format(new Date(), 'yyyy-MM-dd'),
        items: [{}],
      },
      transfer_form: {
        date: format(new Date(), 'yyyy-MM-dd'),
      },
      form_export: {},
      transaction_items_options_income: [],
      transaction_items_options_outcome: [],
      account_options: [],
      account_options_income: [],
      account_options_outcome: [],
      account_options_transfer: [],
      supplier_options: [],
      employee_options: [],
      transaction_to_delete: null,
      transaction_to_edit: null,
      filter_type: '',
      filter_type_options: [
        { value: '', label: 'All' },
        { value: 'income', label: 'Income' },
        { value: 'outcome', label: 'Outcome' },
        { value: 'transfer', label: 'Transfer' },
      ],
      filter_account: null,
      filter_supplier: null,
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form_balance: [],
    }
  },
  validations: {
    form: {
      account: { required },
      date: { required },
      reference: {
        required: requiredIf(function() {
          return this.form.account?.reference
        })
      },
      items: {
        $each: {
          amount: { required },
          item: { required },
        }
      }
    },
    form_balance: {
      $each: {
        account: { required },
        balance: { required },
      }
    },
    transfer_form: {
      from_account: { required },
      to_account: { required },
      from_amount: { required },
      date: { required },
      to_amount: {
        required: requiredIf(function() {
          return this.transfer_form.from_account?.currency?.code !== this.transfer_form.to_account?.currency?.code
        })
      },
    },
    form_export: {
      accounts: { required },
      period: { required },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    this.$nextTick(() => {
      this.fetchItems()
    })
    getFinanceAccounts().then(response => { this.account_options = response.data })
    getFinanceAccounts({ income: true }).then(response => { this.account_options_income = response.data })
    getFinanceAccounts({ outcome: true }).then(response => { this.account_options_outcome = response.data })
    getFinanceAccounts({ transfer: true }).then(response => { this.account_options_transfer = response.data })
    getFinanceTransactionsItemsList({ revenue: true })
      .then(response => {
        this.transaction_items_options_income = response.data
      })
    getFinanceTransactionsItemsList({ expense: true })
      .then(response => {
        this.transaction_items_options_outcome = response.data
      })
    getEmployeesListSearch({ status: 'working_resident' })
      .then(res => { this.employee_options = res.data })
  },
  methods: {
    fetchItems() {
      getFinanceCashflow({
        page: this.pagination.page,
        page_size: 40,
        search: this.search,
        date__gte: this.search ? '' : this.filter_dates.start,
        date__lte: this.search ? '' : this.filter_dates.end,
        type: this.search ? '' : this.filter_type,
        account: this.search ? '' : this.filter_account,
        supplier: this.search ? '' : this.filter_supplier?.id,
      })
        .then(response => {
          this.table_data = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_preloader = false
          this.is_loaded = true
        })
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onCheckBalanceForm() {
      this.$v.form_balance.$touch()
      if (this.$v.form_balance.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormBalance()
      }
    },
    sendFormBalance() {
      this.$refs.submit_balance.preload = true
      postFinanceAccountsBalanceReconciliation(this.form_balance.map(({ balance, account }) => ({ balance, account: account.id })))
        .then(() => {
          this.$modal.hide('balance-modal')
        })
        .catch(() => { this.$refs.submit_balance.preload = false })
    },
    onCheckFormTransfer() {
      this.$v.transfer_form.$touch()
      if (this.$v.transfer_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormTransfer()
      }
    },
    sendFormTransfer() {
      this.$refs.submit_transfer.preload = true
      if (!this.transaction_to_edit) {
        postFinanceCashflowTransfer(this.normalizeFormTransfer())
          .then(() => {
            this.fetchItems()
            this.$refs.submit_transfer.preload = false
            this.$modal.hide('transfer-modal')
          })
          .catch(() => { this.$refs.submit_transfer.preload = false })
      } else {
        putFinanceCashflowTransfer(this.transaction_to_edit.id, this.normalizeEditFormTransfer())
          .then(() => {
            this.fetchItems()
            this.$refs.submit_transfer.preload = false
            this.$modal.hide('transfer-modal')
            this.transaction_to_edit = null
          })
          .catch(() => { this.$refs.submit_transfer.preload = false })
      }
    },
    onShowExportModal() {
      this.form_export = {}
      this.$v.form_export.$reset()
      this.$modal.show('exportModal')
    },
    onCheckFormExport() {
      this.$v.form_export.$touch()
      if (this.$v.form_export.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormExport()
      }
    },
    sendFormExport() {
      this.$refs.submitExport.preload = true
      const data = {
        accounts: this.form_export.accounts,
        date_after: this.form_export.period[0],
        date_before: this.form_export.period[1],
      }
      if (this.form_export.supplier) data.supplier = this.form_export.supplier.id
      getFinanceCashflowExport(data)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Cashflow export ${this.dateFormat(this.form_export.period[0])}-${this.dateFormat(this.form_export.period[1])}.xlsx`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.$modal.hide('exportModal')
        })
        .catch(() => { this.$refs.submitExport.preload = false })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (!this.transaction_to_edit) {
        postFinanceCashflow(this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$refs.submit.preload = false
            this.$modal.hide('transaction-modal')
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        patchFinanceCashflow(this.transaction_to_edit.id, this.normalizeEditForm())
          .then(() => {
            this.fetchItems()
            this.$refs.submit.preload = false
            this.$modal.hide('transaction-modal')
            this.transaction_to_edit = null
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    normalizeForm() {
      const form = {}
      for (const property in this.form) {
        if (this.form[property]) form[property] = this.form[property]
      }
      if (this.transaction_type === 'income') form.type = 1
      if (this.transaction_type === 'outcome') form.type = 2
      if (form.supplier) form.supplier = form.supplier.id
      form.account = form.account.id
      if (form.responsible) form.responsible = form.responsible.id
      form.items = form.items.map(({amount, item}) => {
        return {
          amount,
          item: item.id,
        }
      })

      return form
    },
    normalizeFormTransfer() {
      const form = {}
      for (const property in this.transfer_form) {
        if (this.transfer_form[property]) form[property] = this.transfer_form[property]
      }
      if (form.from_account?.currency?.code === form.to_account?.currency?.code) delete form.to_amount
      form.from_account = form.from_account.id
      form.to_account = form.to_account.id

      return form
    },
    normalizeEditForm() {
      const form = {...this.form}
      form.account = form.account.id
      if (form.responsible) form.responsible = form.responsible.id
      if (form.supplier) form.supplier = form.supplier.id
      form.items = form.items.map(({amount, item}) => {
        return {
          amount,
          item: item.id,
        }
      })

      return form
    },
    normalizeEditFormTransfer() {
      const form = {...this.transfer_form}
      if (form.from_account?.currency?.code === form.to_account?.currency?.code) form.to_amount = null
      form.from_account = form.from_account.id
      form.to_account = form.to_account.id

      return form
    },
    onShowCreateModal(type) {
      this.transaction_type = type
      this.transaction_to_edit = null
      this.form = {
        date: format(new Date(), 'yyyy-MM-dd'),
        items: [{}],
      }
      this.$v.form.$reset()
      this.$modal.show('transaction-modal')
      if (this.transaction_type === 'transfer') this.$modal.show('transfer-modal')
    },
    onShowCreateModalTransfer() {
      this.transaction_to_edit = null
      this.transfer_form = {
        date: format(new Date(), 'yyyy-MM-dd'),
      }
      this.$v.transfer_form.$reset()
      this.$modal.show('transfer-modal')
    },
    onShowEditModal(transaction) {
      this.transaction_to_edit = transaction
      this.transaction_type = transaction.is_transfer ? 'transfer' : transaction.type === 1 ?  'income' : 'outcome'
      if (this.transaction_type !== 'transfer') {
        this.form = {
          type: transaction.type,
          items: transaction.items,
          date: transaction.date,
          account: transaction.account,
          description: transaction.description,
          responsible: transaction.responsible,
          reference: transaction.reference,
          supplier: transaction.supplier,
        }
        this.$modal.show('transaction-modal')
      } else {
        this.transfer_form = {
          date: transaction.date,
          from_amount: transaction.amount,
          to_amount: transaction.transfer_transaction_to?.amount || null,
          from_account: transaction.account,
          to_account: transaction.transfer_transaction_to?.account || null,
          description: transaction.description,
        }
        this.$modal.show('transfer-modal')
      }
    },
    onShowBalanceModal() {
      this.form_balance = [{}]
      this.$v.form_balance.$reset()
      this.$modal.show('balance-modal')
    },
    onShowDeleteModal(transaction, isTransfer) {
      this.transaction_to_delete = transaction
      this.transaction_type = isTransfer ? 'transfer' : 'transaction'
      this.$modal.show('delete-modal')
    },
    onDeleteTransaction() {
      this.$refs.delete.preload = true
      if (this.transaction_type === 'transfer') {
        deleteFinanceCashflowTransfer(this.transaction_to_delete.id)
          .then(() => {
            this.fetchItems()
            this.$refs.delete.preload = false
            this.$modal.hide('delete-modal')
          })
          .catch(() => { this.$refs.delete.preload = false })
      } else {
        deleteFinanceCashflow(this.transaction_to_delete.id)
          .then(() => {
            this.fetchItems()
            this.$refs.delete.preload = false
            this.$modal.hide('delete-modal')
          })
          .catch(() => {
            this.$refs.delete.preload = false
          })
      }
    },
    onAddItem() {
      this.form.items.push({})
    },
    onRemoveItem(index) {
      this.form.items.splice(index, 1)
    },
    onAddBalanceItem() {
      this.form_balance.push({})
    },
    onRemoveBalanceItem(index) {
      this.form_balance.splice(index, 1)
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start && this.filter_dates?.end) {
        this.date_picker_error = false
        this.fetchItems()
      } else this.date_picker_error = true
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : ''
    },
    tooltipContent(transaction) {
      return `${transaction.created_by?.full_name || 'System' }<br>${this.dateTimeFormat(transaction.created_at)}`
    },
    tooltipContentCashflowItems(transaction) {
      let content = ''
      transaction.items.forEach(({item, amount, supplier}) => {
        content += `<span><b>Item:</b> ${transaction.type === 1 ? item?.name_revenue || '' : item?.name_expense || ''} <b>Amount:</b> ${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(amount)} ${transaction.account.currency.code} ${supplier ? `<b>Supplier</b>: ${supplier.legal_name}` : ''}</span>`
      })
      return content
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
    onTransactionSearch() {
      this.searchTransaction(this)
    },
    searchTransaction: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

<style scoped lang="sass">
.transaction__table
  background-color: #fff

  &:before
    content: ''
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    background-color: rgba(#fff, 0.8)
    opacity: 0
    z-index: 2
    pointer-events: none
    transition: opacity 0.3s

.transaction__table--loading:before
  opacity: 1
  pointer-events: auto

.transaction__table-row
  display: grid
  align-items: center
  grid-template-columns: 120px 180px 180px 1fr 250px 185px 80px
  border: 1px solid #eaeaea
  border-top: none

  &:first-child
    border-color: #f6f6f6
    background-color: #f6f6f6
    border-radius: 5px 5px 0 0

  &:nth-child(odd)
    background-color: #f6f6f6

.transaction__table-row--head .transaction__table-col
  font-weight: 500

.transaction__table-col
  padding: 10px

  b
    font-weight: 500

.transaction__table-btn
  font-weight: 500
  text-align: left
  transition: color 0.3s

  &:hover
    color: $color-theme

  span
    color: #626262
    font-size: 13px

.transaction__table-col--flex-col
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 3px

.transaction__table-col--flex
  display: flex
  gap: 10px
  align-items: center
  justify-content: center

  button
    line-height: 0
    transition: transform 0.2s

    &:hover
      transform: translateY(-1px)

    img
      width: 25px
      height: 25px

.transaction__table-amount--green
  color: $color-theme

.transaction__table-amount--red
  color: #e85c20

.transaction__table-description
  color: #626262
  font-size: 13px

.transaction__table-balance
  position: relative
  padding-left: 20px

  &:before
    content: ''
    position: absolute
    left: 8px
    top: 5px
    width: 8px
    height: 5px

.transaction__table-balance--minus:before
    background-image: url("~@/assets/img/balance-minus.svg")

.transaction__table-balance--plus:before
  background-image: url("~@/assets/img/balance-plus.svg")
</style>
